// src/pages/shop/ShopEmployees.jsx
//
// Управление сотрудниками магазина (только для владельца).
// Формы зависят от режима магазина: shop.employeeAuthMode = 'password' | 'pin'.
//   password: name + login + password + permissions
//   pin:      name + pin + permissions

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { employees as employeesApi } from '../../api';
import { errorMessage } from '../../utils/errorMessage';
import Icon from '../../components/Icon';
import SafeInput from '../../components/SafeInput';
import { useToast } from '../../hooks/useToast';
import { useMyShop } from '../../hooks/useMyShop';
import { useDeletePin } from '../../hooks/useDeletePin';
import { employeeModulePerms, employeeActionPerms, isModuleOn } from '../../config/modules';
import PasswordInput from '../../components/PasswordInput';

// Права выводим из реестра модулей (единый источник правды):
//  - права-видимости разделов (по модулю)
//  - права-действия (loyalty/stockOps/discounts/…)
const ALL_MODULE_PERMS = employeeModulePerms();
const ACTION_PERMS = employeeActionPerms();
const ALL_PERMS = [...ALL_MODULE_PERMS, ...ACTION_PERMS];

export default function ShopEmployees() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toastApi = useToast() || {};
  const show = toastApi.show || (() => {});
  const ToastView = toastApi.ToastView || null;
  const shop = useMyShop();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | object

  const authMode = shop?.employeeAuthMode || 'password';

  const load = async () => {
    setLoading(true);
    try {
      const list = await employeesApi.list();
      setItems(Array.isArray(list) ? list : []);
    } catch (e) {
      show(errorMessage(e, t), 'error');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  if (editing) {
    return (
      <EmployeeForm
        employee={editing === 'new' ? null : editing}
        authMode={authMode}
        onCancel={() => setEditing(null)}
        onSaved={(msg) => { setEditing(null); load(); show(msg); }}
        onError={(m) => show(m, 'error')}
      />
    );
  }

  return (
    <div className="page" style={{ paddingBottom: 32 }}>
      {ToastView}

      <div className="page-header">
        <button className="back-btn" onClick={() => navigate(-1)}>
          <Icon name="chevronLeft" size={18} />
        </button>
        <h1 className="page-title">{t('employees.sectionTitle')}</h1>
      </div>

      <div style={{ padding: '0 20px' }}>
        <div style={{
          padding: 10, marginBottom: 16,
          background: 'var(--brand-primary-soft)', color: 'var(--brand-primary)',
          borderRadius: 'var(--r-sm)', fontSize: 12, lineHeight: 1.5,
        }}>
          {authMode === 'pin'
            ? t('employees.modeHintPin')
            : t('employees.modeHintPassword')}
        </div>

        {loading ? (
          <div className="loader"><div className="spinner" /></div>
        ) : items.length === 0 ? (
          <div className="empty-state">
            <div className="icon"><Icon name="users" size={32} stroke={1.5} /></div>
            <p style={{ fontWeight: 700, marginBottom: 4 }}>{t('employees.noEmployees')}</p>
            <p style={{ fontSize: 13, color: 'var(--fg-faint)' }}>
              {t('employees.noEmployeesHint')}
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {items.map(emp => (
              <EmployeeRow key={emp.id} employee={emp} authMode={authMode} onClick={() => setEditing(emp)} />
            ))}
          </div>
        )}

        <button
          className="btn btn-primary btn-lg"
          onClick={() => setEditing('new')}
          style={{ marginTop: 16, width: '100%' }}
        >
          <Icon name="plus" size={16} stroke={2.5} />
          {t('employees.addEmployee')}
        </button>
      </div>
    </div>
  );
}

function EmployeeRow({ employee, authMode, onClick }) {
  const { t } = useTranslation();
  const perms = employee.permissions || {};
  // Считаем только булевы true-права, лимиты (числа) не считаем
  const grantedCount = ALL_PERMS.filter(p => perms[p.key] === true).length;

  const credentialDisplay = authMode === 'pin'
    ? (employee.pin ? `PIN: ${employee.pin}` : '—')
    : (employee.login || '—');

  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '12px 14px', borderRadius: 'var(--r-sm)',
        background: 'var(--bg-elevated)', border: '1px solid var(--border)',
        cursor: 'pointer',
      }}
    >
      <div style={{
        width: 40, height: 40, borderRadius: '50%',
        background: 'var(--brand-primary-soft)', color: 'var(--brand-primary)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: 800, fontSize: 16, flexShrink: 0,
      }}>
        {(employee.name?.[0] || '?').toUpperCase()}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: 14, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {employee.name || '—'}
          {employee.role === 'admin' && (
            <span style={{
              marginLeft: 8,
              fontSize: 10, fontWeight: 700,
              color: 'var(--brand-primary)',
              background: 'var(--brand-primary-soft)',
              padding: '2px 6px', borderRadius: 4,
              textTransform: 'uppercase', letterSpacing: '0.05em',
            }}>
              {t('employees.roleAdminBadge')}
            </span>
          )}
        </p>
        <p style={{ fontSize: 12, color: 'var(--fg-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'var(--font-mono)' }}>
          {credentialDisplay}
        </p>
        <p style={{ fontSize: 11, color: 'var(--fg-faint)', marginTop: 2 }}>
          {employee.role === 'admin'
            ? t('employees.roleAdminBadgeFull')
            : (grantedCount === 0
                ? '—'
                : ALL_PERMS.filter(p => perms[p.key] === true).map(p => t(p.labelKey).split(' ')[0]).join(', '))}
        </p>
      </div>
      <Icon name="chevronRight" size={16} color="var(--fg-faint)" />
    </div>
  );
}

function EmployeeForm({ employee, authMode, onCancel, onSaved, onError }) {
  const { t } = useTranslation();
  const { confirm: confirmDelete, PinModal } = useDeletePin();
  const shop = useMyShop();
  const isNew = !employee;
  const isPinMode = authMode === 'pin';

  // Показываем права-видимости только для включённых у магазина модулей
  const modulePerms = ALL_MODULE_PERMS.filter(p => isModuleOn(shop, p.key));

  const [name, setName] = useState(employee?.name || '');
  const [login, setLogin] = useState(employee?.login || '');
  const [pin, setPin] = useState(employee?.pin || '');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState(employee?.role || 'employee');
  const [permissions, setPermissions] = useState(() => {
    const initial = {};
    // Чистая гранулярность: показываем ровно то, что выдано (без авто-разворота).
    const src = employee?.permissions || {};
    for (const k of Object.keys(src)) {
      const v = src[k];
      // Булевы права и числовые лимиты (maxDiscount*)
      if (v === true || typeof v === 'number') initial[k] = v;
    }
    return initial;
  });
  const [saving, setSaving] = useState(false);

  const togglePerm = (key) => {
    setPermissions(prev => {
      const next = { ...prev };
      if (next[key] === true) {
        delete next[key];
        // Если выключили "Скидки" — убираем лимиты тоже
        if (key === 'discounts') {
          delete next.maxDiscountPercent;
          delete next.maxDiscountAmount;
        }
      } else {
        next[key] = true;
        // Если включили "Скидки" — ставим разумные дефолты для лимитов
        if (key === 'discounts') {
          if (next.maxDiscountPercent == null) next.maxDiscountPercent = 10;
          if (next.maxDiscountAmount == null)  next.maxDiscountAmount  = 0;
        }
      }
      return next;
    });
  };

  const save = async () => {
    if (!name.trim()) return onError(t('employees.errorNameRequired'));

    if (isPinMode) {
      if (!pin) return onError(t('employees.errorPinRequired'));
      if (!/^[0-9]{4,6}$/.test(pin)) return onError(t('employees.errorPinFormat'));
    } else {
      if (isNew) {
        if (!login.trim()) return onError(t('employees.errorLoginRequired'));
        if (!password) return onError(t('employees.errorPassRequired'));
        if (password.length < 6) return onError(t('auth.errorPasswordShort'));
      } else if (password && password.length < 6) {
        return onError(t('auth.errorPasswordShort'));
      }
      if (login.trim() && !/^[a-zA-Z0-9_]{3,30}$/.test(login.trim())) {
        return onError(t('employees.errorLoginFormat'));
      }
    }

    setSaving(true);
    // Нормализуем permissions перед отправкой:
    // - empty string в числовых лимитах → 0
    // - убираем лимиты если discounts не разрешено
    const normalizedPermissions = (() => {
      const out = {};
      for (const k of Object.keys(permissions)) {
        const v = permissions[k];
        if (v === true) out[k] = true;
        else if (typeof v === 'number') out[k] = v;
        else if (v === '' || v == null) out[k] = 0;
      }
      if (out.discounts !== true) {
        delete out.maxDiscountPercent;
        delete out.maxDiscountAmount;
      }
      return out;
    })();

    try {
      if (isNew) {
        const data = {
          name: name.trim(),
          permissions: normalizedPermissions,
          role,
        };
        if (isPinMode) {
          data.pin = pin;
        } else {
          data.login = login.trim().toLowerCase();
          data.password = password;
        }
        await employeesApi.create(data);
        onSaved(t('employees.created'));
      } else {
        const data = { name: name.trim(), permissions: normalizedPermissions, role };
        if (isPinMode) {
          if (pin !== employee.pin) data.pin = pin;
        } else {
          if (login.trim().toLowerCase() !== employee.login) data.login = login.trim().toLowerCase();
          if (password) data.password = password;
        }
        await employeesApi.update(employee.id, data);
        onSaved(t('employees.saved'));
      }
    } catch (e) {
      onError(errorMessage(e, t));
      setSaving(false);
    }
  };

  const remove = async () => {
    const ok = await confirmDelete({
      title: t('employees.deleteTitle', 'Удалить сотрудника?'),
      message: t('employees.confirmRemove'),
    });
    if (!ok) return;
    setSaving(true);
    try {
      await employeesApi.remove(employee.id);
      onSaved(t('employees.removed'));
    } catch (e) {
      onError(errorMessage(e, t));
      setSaving(false);
    }
  };

  return (
    <div className="page" style={{ paddingBottom: 32 }}>
      <div className="page-header">
        <button className="back-btn" onClick={onCancel}>
          <Icon name="chevronLeft" size={18} />
        </button>
        <h1 className="page-title">
          {isNew ? t('employees.newEmployee') : t('employees.editEmployee')}
        </h1>
      </div>

      <div style={{ padding: '0 20px' }}>
        <div className="input-group">
          <label>{t('employees.employeeName')}</label>
          <SafeInput
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder={t('auth.namePlaceholder')}
          />
        </div>

        {isPinMode ? (
          <div className="input-group">
            <label>{t('employees.employeePin')}</label>
            <input
              className="input"
              type="tel"
              inputMode="numeric"
              value={pin}
              onChange={e => setPin(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
              placeholder="1234"
              maxLength={6}
              style={{ fontFamily: 'var(--font-mono)', letterSpacing: '0.2em' }}
              autoComplete="off"
            />
            <p style={{ fontSize: 11, color: 'var(--fg-faint)', marginTop: 4 }}>
              {t('employees.pinHint')}
            </p>
          </div>
        ) : (
          <>
            <div className="input-group">
              <label>{t('employees.employeeLogin')}</label>
              <SafeInput
                value={login}
                onChange={e => setLogin(e.target.value)}
                placeholder="kassa1"
                style={{ fontFamily: 'var(--font-mono)' }}
              />
              <p style={{ fontSize: 11, color: 'var(--fg-faint)', marginTop: 4 }}>
                {t('employees.loginHint')}
              </p>
            </div>

            <div className="input-group">
              <label>
                {isNew ? t('employees.employeePassword') : t('employees.changePassword')}
              </label>
              <PasswordInput
                className="input"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder={isNew ? '••••••' : '••••••'}
                autoComplete="new-password"
              />
            </div>
          </>
        )}

        {/* Тип сотрудника: обычный или администратор */}
        <div className="card" style={{ marginTop: 8, marginBottom: 8 }}>
          <p style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>
            {t('employees.roleLabel')}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
              <input
                type="radio"
                name="employee-role"
                checked={role === 'employee'}
                onChange={() => setRole('employee')}
                style={{ marginTop: 3, flexShrink: 0 }}
              />
              <div>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{t('employees.roleEmployee')}</div>
                <div style={{ fontSize: 11, color: 'var(--fg-faint)', marginTop: 2 }}>
                  {t('employees.roleEmployeeHint')}
                </div>
              </div>
            </label>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
              <input
                type="radio"
                name="employee-role"
                checked={role === 'admin'}
                onChange={() => setRole('admin')}
                style={{ marginTop: 3, flexShrink: 0 }}
              />
              <div>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{t('employees.roleAdmin')}</div>
                <div style={{ fontSize: 11, color: 'var(--fg-faint)', marginTop: 2 }}>
                  {t('employees.roleAdminHint')}
                </div>
              </div>
            </label>
          </div>
        </div>

        {role === 'admin' ? (
          <div className="card" style={{ marginTop: 8, background: 'var(--bg-subtle)' }}>
            <p style={{ fontSize: 13, color: 'var(--fg-muted)', textAlign: 'center', margin: 0 }}>
              {t('employees.adminFullAccessNote')}
            </p>
          </div>
        ) : (
        <div className="card" style={{ marginTop: 8 }}>
          <p style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>
            {t('employees.permissions')}
          </p>
          <p style={{ fontSize: 11, color: 'var(--fg-faint)', marginBottom: 12 }}>
            {t('employees.permissionsHint')}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Разделы — что сотрудник ВИДИТ (авто из реестра модулей) */}
            {modulePerms.length > 0 && (
              <div>
                <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--fg-muted)', marginBottom: 8 }}>
                  {t('employees.permSectionModules')}
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {modulePerms.map(p => (
                    <label key={p.key} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={permissions[p.key] === true}
                        onChange={() => togglePerm(p.key)}
                        style={{ margin: 0, flexShrink: 0 }}
                      />
                      <span style={{ fontSize: 14 }}>{t(p.labelKey)}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Действия — что сотрудник может ДЕЛАТЬ (авто из реестра модулей) */}
            <div>
              <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--fg-muted)', marginBottom: 8 }}>
                {t('employees.permSectionActions')}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {ACTION_PERMS.map(p => (
                  <div key={p.key}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={permissions[p.key] === true}
                        onChange={() => togglePerm(p.key)}
                        style={{ margin: 0, flexShrink: 0 }}
                      />
                      <span style={{ fontSize: 14 }}>{t(p.labelKey)}</span>
                    </label>

                {/* Под чекбоксом "Скидки" — поля лимитов */}
                {p.key === 'discounts' && permissions.discounts === true && (
                  <div style={{
                    marginLeft: 24, marginTop: 8,
                    display: 'flex', flexDirection: 'column', gap: 8,
                    padding: 10,
                    background: 'var(--bg-subtle)',
                    borderRadius: 'var(--r-xs)',
                  }}>
                    <div>
                      <label style={{ fontSize: 12, color: 'var(--fg-muted)', display: 'block', marginBottom: 4 }}>
                        {t('discounts.maxPercentLabel')}
                      </label>
                      <input
                        className="input"
                        type="number"
                        min={0}
                        max={100}
                        step={1}
                        value={permissions.maxDiscountPercent ?? ''}
                        onChange={e => {
                          const v = e.target.value;
                          setPermissions(prev => ({
                            ...prev,
                            maxDiscountPercent: v === '' ? '' : Math.max(0, Math.min(100, Number(v))),
                          }));
                        }}
                        onBlur={() => {
                          // При потере фокуса — если поле пустое, ставим 0 (= запрет)
                          if (permissions.maxDiscountPercent === '' || permissions.maxDiscountPercent == null) {
                            setPermissions(prev => ({ ...prev, maxDiscountPercent: 0 }));
                          }
                        }}
                        inputMode="decimal"
                      />
                      <p style={{ fontSize: 10, color: 'var(--fg-faint)', marginTop: 2 }}>
                        {t('discounts.maxPercentHint')}
                      </p>
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: 'var(--fg-muted)', display: 'block', marginBottom: 4 }}>
                        {t('discounts.maxAmountLabel')}
                      </label>
                      <input
                        className="input"
                        type="number"
                        min={0}
                        step={1}
                        value={permissions.maxDiscountAmount ?? ''}
                        onChange={e => {
                          const v = e.target.value;
                          setPermissions(prev => ({
                            ...prev,
                            maxDiscountAmount: v === '' ? '' : Math.max(0, Number(v)),
                          }));
                        }}
                        onBlur={() => {
                          if (permissions.maxDiscountAmount === '' || permissions.maxDiscountAmount == null) {
                            setPermissions(prev => ({ ...prev, maxDiscountAmount: 0 }));
                          }
                        }}
                        inputMode="decimal"
                      />
                      <p style={{ fontSize: 10, color: 'var(--fg-faint)', marginTop: 2 }}>
                        {t('discounts.maxAmountHint')}
                      </p>
                    </div>
                  </div>
                )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        )}

        <button
          className="btn btn-primary btn-lg"
          onClick={save}
          disabled={saving}
          style={{ width: '100%', marginTop: 16 }}
        >
          {saving ? '…' : (isNew ? t('common.create') : t('common.save'))}
        </button>

        {!isNew && (
          <button
            onClick={remove}
            disabled={saving}
            className="btn btn-ghost"
            style={{ width: '100%', marginTop: 8, color: 'var(--danger)' }}
          >
            <Icon name="trash" size={14} />
            {t('common.delete')}
          </button>
        )}
      </div>
      {PinModal}
    </div>
  );
}
